/**
 * Telemetry (PRD §15.4): GA4 events from the main thread via gtag, or batched through the
 * /qsvp/collect relay. Never Partytown, never a second gtag.js, never our own client ID.
 */

/** Never sent to GA4 (§15.4 "Not sent"): creative IDs, advertiser names, bid prices. */
const BLOCKED_PARAMS = /creative|advertiser|line_?item|bid|price|hb_/i;

/** Events throttled to one per 2 s (§15.4 volume rules). */
const THROTTLED = new Set(['qsvp_play', 'qsvp_pause', 'qsvp_seek']);

/**
 * Read a cookie value.
 * @param {string} cookieString document.cookie
 * @param {string} name
 * @returns {string|null}
 */
export function readCookie(cookieString, name) {
  const match = String(cookieString || '')
    .split(/;\s*/)
    .find((part) => part.startsWith(name + '='));
  return match ? decodeURIComponent(match.slice(name.length + 1)) : null;
}

/**
 * GA client_id from the _ga cookie ("GA1.1.123.456" -> "123.456").
 * @param {string|null} value
 * @returns {string|null}
 */
export function parseClientId(value) {
  const parts = String(value || '').split('.');
  return parts.length >= 4 ? parts.slice(-2).join('.') : null;
}

/**
 * session_id from the _ga_<container> cookie, GS1 ("GS1.1.1700000000.3.1...") or
 * GS2 ("GS2.1.s1700000000$o3$g1...") format.
 * @param {string|null} value
 * @returns {string|null}
 */
export function parseSessionId(value) {
  const text = String(value || '');
  if (text.startsWith('GS1.')) return text.split('.')[2] || null;
  if (text.startsWith('GS2.')) {
    const match = text.match(/^GS2\.\d+\.s(\d+)/);
    return match ? match[1] : null;
  }
  return null;
}

/**
 * True when GA4 on the page runs inside Partytown (§15.4).
 * @param {Document} doc
 * @param {Window} win
 * @returns {boolean}
 */
export function isPartytownGa(doc, win) {
  const scripts = doc.querySelectorAll('script[type="text/partytown"]');
  for (const script of scripts) {
    if (/googletagmanager\.com\/(gtag\/js|gtm\.js)/.test(script.src || script.textContent || '')) return true;
  }
  const forward = (win.partytown && win.partytown.forward) || [];
  return forward.some((entry) => /gtag|dataLayer/.test(String(Array.isArray(entry) ? entry[0] : entry)));
}

/**
 * Measurement ID from config, else from the page's gtag.js URL (including Partytown tags).
 * @param {object} analytics config.analytics
 * @param {Document} doc
 * @returns {string|null}
 */
export function findMeasurementId(analytics, doc) {
  if (analytics.measurementId && !/X{4,}/.test(analytics.measurementId)) return analytics.measurementId;
  for (const script of doc.querySelectorAll('script[src*="googletagmanager.com/gtag/js"]')) {
    const match = /[?&]id=(G-[A-Z0-9]+)/.exec(script.src);
    if (match) return match[1];
  }
  return null;
}

/**
 * Pick the delivery mode for this page view (§15.4 table).
 * @param {object} input
 * @param {object} input.analytics config.analytics
 * @param {Window} input.win
 * @param {Document} input.doc
 * @param {boolean} input.analyticsAllowed consent
 * @returns {{mode:'gtag'|'relay'|'off', measurementId:string|null}}
 */
export function pickMode({ analytics, win, doc, analyticsAllowed }) {
  const measurementId = findMeasurementId(analytics, doc);
  if (!analyticsAllowed || analytics.mode === 'off' || !measurementId) return { mode: 'off', measurementId };
  const partytown = isPartytownGa(doc, win);
  const gtagReady = typeof win.gtag === 'function' && !partytown;
  if (analytics.mode !== 'relay' && gtagReady) return { mode: 'gtag', measurementId };
  if (analytics.mode === 'gtag') return { mode: 'off', measurementId };
  if (!readCookie(doc.cookie, '_ga') || !analytics.relayUrl) return { mode: 'off', measurementId };
  return { mode: 'relay', measurementId };
}

/**
 * Create the telemetry sink.
 * @param {object} deps
 * @param {Window} deps.win
 * @param {Document} deps.doc
 * @param {object} deps.config final config
 * @param {Function} deps.now
 * @param {Function} deps.setIntervalFn
 * @param {Function} deps.getCommon () => common params
 * @param {Function} [deps.debugLog] (name, params) => void
 * @returns {object}
 */
export function createTelemetry({ win, doc, config, now, setIntervalFn, getCommon, debugLog = () => {} }) {
  let mode = 'off';
  let measurementId = null;
  const queue = [];
  const lastSent = new Map();
  let lastEngagement = now();

  /** Send queued relay events (≤ 25 per request). */
  function flush(useBeacon) {
    if (mode !== 'relay' || !queue.length) return;
    const clientId = parseClientId(readCookie(doc.cookie, '_ga'));
    if (!clientId) {
      queue.length = 0; // never create our own client ID
      return;
    }
    const container = (measurementId || '').replace(/^G-/, '');
    const sessionId = parseSessionId(readCookie(doc.cookie, '_ga_' + container));
    while (queue.length) {
      const events = queue.splice(0, 25).map((event) => {
        if (sessionId) event.params.session_id = sessionId;
        return event;
      });
      const body = JSON.stringify({ measurement_id: measurementId, client_id: clientId, events });
      const url = config.analytics.relayUrl;
      try {
        if (useBeacon && win.navigator && typeof win.navigator.sendBeacon === 'function') {
          win.navigator.sendBeacon(url, body);
        } else if (typeof win.fetch === 'function') {
          win.fetch(url, {
            method: 'POST',
            body,
            keepalive: true,
            credentials: 'same-origin',
            headers: { 'content-type': 'application/json' },
          });
        }
      } catch (error) {
        // Telemetry must never break the player.
      }
    }
  }

  return {
    /** @returns {string} */
    get mode() {
      return mode;
    },
    /**
     * Start once consent is known.
     * @param {boolean} analyticsAllowed
     */
    start(analyticsAllowed) {
      const picked = pickMode({ analytics: config.analytics, win, doc, analyticsAllowed });
      mode = picked.mode;
      measurementId = picked.measurementId;
      if (mode === 'relay') {
        setIntervalFn(() => flush(false), 5000);
        win.addEventListener('pagehide', () => flush(true));
      }
    },
    /**
     * Track one event.
     * @param {string} name
     * @param {object} [params]
     */
    track(name, params = {}) {
      const merged = Object.assign({}, getCommon(), params);
      const clean = {};
      for (const key of Object.keys(merged)) if (!BLOCKED_PARAMS.test(key) && merged[key] !== undefined) clean[key] = merged[key];
      debugLog(name, clean);
      if (THROTTLED.has(name)) {
        const last = lastSent.get(name) || -Infinity;
        if (now() - last < 2000) return;
        lastSent.set(name, now());
      }
      if (mode === 'gtag') {
        try {
          win.gtag('event', name, Object.assign({ send_to: measurementId }, clean));
        } catch (error) {
          // Ignore.
        }
      } else if (mode === 'relay') {
        const time = now();
        clean.engagement_time_msec = Math.max(1, time - lastEngagement);
        lastEngagement = time;
        queue.push({ name, params: clean });
        if (queue.length >= 25) flush(false);
      }
    },
    flush,
  };
}
